import { ref, computed, watch, onMounted, inject } from 'vue'
import { useStore } from 'vuex'
import { SubmissionFileType, SubmissionFileState, SubmissionFile, ShowToastType, DasOption } from '@/types'
import validateUploadFilesView, { validateDuplicateFiles, validateFiles } from '../services/validation/uploadFiles'
import Guid from '../utils/Guid'
import { DasService } from '../services/das'
import { trackEvent } from '../services/gtm'
import { AxiosError } from 'axios'
import { STANDARD_DAS_TEXT } from '@/constants/das'
import { validateField } from '@/services/validation/utils'
import stripHtml from '@/utils/stripHtml'
import { MAX_FILENAME_LENGTH } from '@/constants/app'
import limits from '../constants/limits'

/**
 * Composable for managing DAS (Data Availability Statement) upload functionality.
 * Handles file uploads, TipTap editor content, and review DAS options.
 * @param emit - Vue emit function for parent component communication
 * @param props - Component props containing error state
 * @returns Object with reactive properties and methods for DAS management
 */
export function useDasUpload(emit: (event: string, ...args: unknown[]) => void, props: { error?: string }) {
  const store = useStore()
  const formatContent = (html: string) => html
  const showToast = inject('showToast') as (options: ShowToastType) => void

  const dasError = ref<string | undefined>(undefined)
  const file = ref(null)
  const isDasCustomFilled = ref(false)
  const hasCustomDasError = ref(false)
  const hasReviewDasError = ref(false)
  const hasUploadDasError = ref(false)
  const activeTab = ref(0)
  const isDasTypeSelected = ref<boolean | null>(null)
  const editorKey = ref(0)
  const isUploadButtonLoading = ref(false)
  const dasIsLoading = ref(false)

  const selectedOption = computed({
    get: () => store.state.das.selectedOption,
    set: value => store.dispatch('changeDASOption', value),
  })

  const dasFileIsUploaded = computed(() => store.state.das.dasFileIsUploaded)
  const dasCustomIsSent = computed(() => store.state.das.dasCustomIsSent)

  const dasCustomContent = computed({
    get() {
      return store.state.das.dasCustomContent
    },
    set(value: string) {
      store.commit('setDasCustomContent', value)
    },
  })

  const editDasFile = computed(() => {
    return !dasFileIsUploaded.value && !dasCustomIsSent.value
  })

  const dasType = computed<boolean | null>({
    get(): boolean | null {
      return dasFileIsUploaded.value || dasCustomIsSent.value || selectedOption.value == DasOption.File || selectedOption.value == DasOption.Custom || isDasTypeSelected.value
    },
    set(val: boolean | null) {
      if (val === true) {
        isDasTypeSelected.value = true
        store.commit('setDasReview', false)
        if (!dasFileIsUploaded.value) {
          activeTab.value = 0
          selectedOption.value = DasOption.Custom
        }
        else if (dasFileIsUploaded.value) {
          activeTab.value = 1
          selectedOption.value = DasOption.File
        }
      }
      if (val === false) {
        isDasTypeSelected.value = false
        store.commit('setDasReview', true)
        handleReviewDas()
      }
    },
  })

  const isDasCustomButtonDisabled = computed(() => {
    const hasContent = (value: string) => {
      const cleanedValue = value.replace(/<p>\s*<\/p>/g, '')
      return cleanedValue.trim().length > 0
    }
    const exceedsLimit = (value: string) => {
      const textOnly = stripHtml(value || '')
      return textOnly.length > limits.DAS.characters.statement
    }

    return !dasCustomContent.value
      || store.state.das.selectedOption !== DasOption.Custom
      || !hasContent(dasCustomContent.value)
      || exceedsLimit(dasCustomContent.value)
  })

  watch(selectedOption, async (newValue, oldValue) => {
    if (newValue === DasOption.Custom) {
      hasReviewDasError.value = false
      store.commit('setDasCustomSelected', true)
      validateUploadFilesView(store)
    }
    else if (newValue === DasOption.File) {
      store.commit('setDasCustomSelected', false)
      store.commit('setDasCustomIsSent', false)
    }
    else if (newValue === DasOption.Review) {
      store.commit('setDasCustomIsSent', false)
      store.commit('setDasCustomSelected', false)
    }
    if (props.error) {
      emit('clear-error')
      emit('update-error')
    }
    if (oldValue === DasOption.Review && (newValue == DasOption.File || newValue == DasOption.Custom)) {
      hasReviewDasError.value = false
    }
    if (oldValue === DasOption.Custom && newValue !== DasOption.Custom) {
      hasCustomDasError.value = false
    }
  })

  onMounted(() => {
    if (dasFileIsUploaded.value) {
      activeTab.value = 1
    }
    else {
      activeTab.value = 0
    }
    if (selectedOption.value === DasOption.Review) {
      dasType.value = false
    }
  })

  const handleTabclick = async (index: number) => {
    activeTab.value = index
    if (index === 0) {
      selectedOption.value = DasOption.Custom
    }
    else if (index === 1) {
      selectedOption.value = DasOption.File
    }
  }

  const handleEditCustomDas = async () => {
    try {
      await store.dispatch('changeDASOption', DasOption.Custom)
      const existingDasFileIndex = store.state.activeSubmission.files.findIndex(
        (file: SubmissionFile) => file.type === SubmissionFileType.DAS,
      )
      if (existingDasFileIndex !== -1) {
        store.state.activeSubmission.files.splice(existingDasFileIndex, 1)
      }
      store.commit('setDasCustomIsSent', false)
    }
    catch (error) {
      trackDasErrorEvent(error, 'editCustomDasError')
    }
  }

  const removeExistingDasFile = () => {
    const existingDasFileIndex = store.state.activeSubmission.files.findIndex(
      (file: SubmissionFile) => file.type === SubmissionFileType.DAS,
    )
    if (existingDasFileIndex !== -1) {
      store.state.activeSubmission.files.splice(existingDasFileIndex, 1)
    }
  }

  const validateFileForUpload = async (file: File) => {
    const duplicateValidation = validateDuplicateFiles([file])
    const fileValidation = validateFiles([file], 'das')
    const [resultDuplicate, resultFiles] = await Promise.all([duplicateValidation, fileValidation])
    if (resultFiles.valid !== true || resultDuplicate.valid !== true) {
      const errorType = !resultFiles.valid ? resultFiles.errorType : resultDuplicate.errorType
      const errorMessages = !resultFiles.valid ? resultFiles.errors : resultDuplicate.errors
      throw { errorType, errorMessages: errorMessages.toString() }
    }
  }

  const showSuccessToast = (message: string, fileType: string) => {
    showToast({
      variant: 'success',
      headingText: 'Success',
      bodyContent: message,
      location: SubmissionFileType.DAS,
      isManual: false,
      isUpload: true,
      fileType,
    })
  }

  /**
   * Handles the action based on selected DAS option.
   * @param fileArray - Array of files for upload option
   */
  const handleAction = async (fileArray: File[] | null = null) => {
    if (selectedOption.value === DasOption.File) {
      await handleUploadDas(fileArray)
    }
    else if (selectedOption.value === DasOption.Custom) {
      await handleCustomDas()
    }
  }

  /**
   * Handles uploading a DAS file.
   * @param fileArray - Array containing the file to upload
   */
  const handleUploadDas = async (fileArray: File[] | null) => {
    isUploadButtonLoading.value = true
    const inputFiles = fileArray as File[] | null
    if (!inputFiles || !inputFiles[0]) {
      throw new Error('No file selected')
    }
    const file = inputFiles[0] as File
    const fileType = file.name.split('.').pop() || 'unknown'
    try {
      dasError.value = undefined
      if (file.name.length > MAX_FILENAME_LENGTH) {
        dasError.value = `${file.name}: File name must be ${MAX_FILENAME_LENGTH} characters or fewer. Current length: ${file.name.length}.`
        isUploadButtonLoading.value = false
        trackEvent({
          category: 'Error',
          action: 'select file',
          event: `UploadDASFileError`,
          location: SubmissionFileType.DAS,
          errorType: 'FilenameTooLong',
          errorDetails: 'Character limit exceeded',
          submissionId: store.state.activeSubmission.submissionId,
        })
        return
      }
      await validateFileForUpload(file)
      await DasService.uploadFile(store, file)
        .then(() => {
          store.commit('setDasFileIsUploaded', true)
          emit('clear-error')
          showSuccessToast('Your data availability statement has been successfully added.', fileType)
          isUploadButtonLoading.value = false
          hasUploadDasError.value = false
        })
        .catch((error) => {
          store.commit('removeSubmissionFile', { fileType: SubmissionFileType.DAS, fileName: file.name })
          store.commit('setDasFileIsUploaded', false)
          isUploadButtonLoading.value = false
          hasUploadDasError.value = true
          trackDasErrorEvent(error, 'uploadDasError', fileType)
        })
    }
    catch (error: unknown) {
      emit('clear-error')
      const err = error as { errorType?: string, errorMessages?: string }
      // Show validation errors inline instead of as toast for consistency
      dasError.value = err.errorMessages || 'An error occurred while validating the file. Please try again.'
      isUploadButtonLoading.value = false
    }
  }

  const handleCustomDas = async () => {
    try {
      const content = store.state.das.dasCustomContent
      const tempId = Guid.generate()
      const newFile = {
        id: tempId,
        file: content,
        type: SubmissionFileType.DAS,
        state: SubmissionFileState.Uploading,
      }

      if (!store.state.activeSubmission.files.some((file: SubmissionFile) => file.type === SubmissionFileType.DAS)) {
        store.commit('_addFile', newFile)
        await DasService.uploadDas(store, store.state.activeSubmission.submissionId, content, tempId)
        store.commit('setDasCustomIsSent', true)
        await validateUploadFilesView(store)
        emit('clear-error')
        showToast({
          variant: 'success',
          headingText: 'Success',
          bodyContent: 'Your data availability statement has been successfully added.',
          location: SubmissionFileType.DAS,
          isManual: false,
          isUpload: false,
        })
        hasCustomDasError.value = false
      }
    }
    catch (error) {
      store.commit('removeSubmissionFile', { fileType: SubmissionFileType.DAS, fileName: store.state.das.dasCustomContent })
      hasCustomDasError.value = true
      trackDasErrorEvent(error, 'customDasError')
    }
  }

  /**
   * Handles adding the standard review DAS text.
   */
  const handleReviewDas = async () => {
    try {
      dasIsLoading.value = true
      await store.dispatch('changeDASOption', DasOption.Review)
      const tempId = Guid.generate()
      const newFile = {
        id: tempId,
        file: STANDARD_DAS_TEXT,
        type: SubmissionFileType.DAS,
        state: SubmissionFileState.Uploading,
      }
      if (!store.state.activeSubmission.files.some((file: SubmissionFile) => file.type === SubmissionFileType.DAS)) {
        store.commit('_addFile', newFile)
        await store.dispatch('uploadDas', { submissionId: store.state.activeSubmission.submissionId, tempId })
      }
      hasReviewDasError.value = false
      emit('clear-error')
    }
    catch (error) {
      store.commit('removeSubmissionFile', { fileType: SubmissionFileType.DAS, fileName: STANDARD_DAS_TEXT })
      hasReviewDasError.value = true
      trackDasErrorEvent(error, 'noDataDasError')
      unsetDasType()
    }
    finally {
      dasIsLoading.value = false
    }
  }

  const handleTextChange = async (
    { html }: { html: string },
    field: { onChange: (value: string) => void },
  ) => {
    dasCustomContent.value = html
    store.commit('setDasCustomContent', html)
    field.onChange(html)
    await validateUploadFilesView(store)
  }

  function isAxiosError(error: unknown): error is AxiosError {
    return typeof error === 'object' && error !== null && 'isAxiosError' in error
  }

  const trackDasErrorEvent = (error: unknown, eventType: string, fileType?: string) => {
    const status = isAxiosError(error) ? error.response?.status : undefined
    const message = isAxiosError(error) ? error.message : undefined
    trackEvent({
      category: 'Server Errors',
      action: 'error',
      event: eventType,
      fileType: fileType,
      location: 'DAS Upload',
      errorType: status ? `HTTP${status}` : 'UnknownError',
      errorDetails: message || 'No error message available',
      submissionId: store.state.activeSubmission.submissionId,
    })
  }

  watch(dasCustomContent, async (newVal) => {
    const result = await validateField(
      newVal,
      `max-characters-length:${limits.DAS.characters.statement}, Data Availability Statement must not exceed`,
    )
    dasError.value = result?.errors ?? undefined
    emit('update-error')
  })

  /**
   * Utility to fully unset dasType and related state so radio is visually unchecked.
   */
  function unsetDasType() {
    isDasTypeSelected.value = null
    selectedOption.value = ''
    store.commit('setDasFileIsUploaded', false)
    store.commit('setDasCustomIsSent', false)
    store.commit('setDasReview', false)
  }

  return {
    // State
    file,
    isDasCustomFilled,
    hasCustomDasError,
    hasReviewDasError,
    hasUploadDasError,
    activeTab,
    isDasTypeSelected,
    editorKey,
    isUploadButtonLoading,
    selectedOption,
    dasFileIsUploaded,
    dasCustomIsSent,
    dasCustomContent,
    editDasFile,
    dasType,
    isDasCustomButtonDisabled,
    // Methods
    handleTabclick,
    handleEditCustomDas,
    removeExistingDasFile,
    formatContent,
    handleAction,
    handleUploadDas,
    handleCustomDas,
    handleReviewDas,
    handleTextChange,
    trackDasErrorEvent,
    dasError,
    unsetDasType,
    dasIsLoading,
  }
}
